import { Component } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

export class AdminErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[AdminErrorBoundary] Caught error:", error);
    console.error("[AdminErrorBoundary] Component stack:", errorInfo?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#FAF8F0] flex items-center justify-center p-6" data-testid="admin-error-boundary">
          <div className="bg-white border border-[#E8E2D8] p-10 max-w-md w-full text-center">
            <AlertTriangle className="w-10 h-10 text-[#C9A0A0] mx-auto mb-4" />
            <h2 className="text-xl text-[#1A1A1A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Something went wrong
            </h2>
            <p className="text-sm text-[#7A7A7A] mb-6 leading-relaxed">
              An unexpected error occurred in the admin dashboard. Please reload the page. If the issue persists, contact support.
            </p>
            {this.state.error && (
              <pre className="text-[10px] text-left bg-[#FAF8F0] border border-[#E8E2D8] p-3 mb-6 overflow-auto max-h-24 text-[#7A7A7A]">
                {this.state.error.toString()}
              </pre>
            )}
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#9A7B4F] text-white text-sm hover:bg-[#8a6b3f] transition-colors"
              data-testid="admin-error-reload-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function AdminLoadingState({ message = "Loading..." }) {
  return (
    <div className="flex items-center justify-center py-20" data-testid="admin-loading-state">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#9A7B4F]/20 border-t-[#9A7B4F] rounded-full animate-spin mx-auto mb-4" />
        <p className="text-sm text-[#7A7A7A]">{message}</p>
      </div>
    </div>
  );
}

export function AdminApiError({ message = "Failed to load data. Please refresh or contact support.", onRetry }) {
  return (
    <div className="bg-[#FBF5EE] border border-[#E8DDD0] px-4 py-3 mb-4 flex items-center justify-between gap-3" data-testid="admin-api-error">
      <div className="flex items-center gap-2 min-w-0">
        <AlertTriangle className="w-4 h-4 text-[#C9A0A0] flex-shrink-0" />
        <p className="text-sm text-[#4A4A4A] truncate">{message}</p>
      </div>
      {onRetry && (
        <button onClick={onRetry} className="text-xs text-[#9A7B4F] hover:underline flex-shrink-0 flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Retry
        </button>
      )}
    </div>
  );
}
